import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addEdge, applyEdgeChanges, applyNodeChanges, type Connection, type EdgeChange, type IsValidConnection, type NodeChange, type NodeRemoveChange, type ReactFlowInstance } from 'reactflow';
import { createFilterNode, FILTER_PRESETS, type FilterPreset } from './data/filterCatalog';
import { buildFFmpegCommand, type ShellTarget } from './lib/commandBuilder';
import { parseFFmpegCommand } from './lib/commandParser';
import { connectInputToOutput, createInputNode, insertPreset, isStreamConnectionValid, layoutWorkflow, nextNodeId, removeWorkflowNode, reorderStreamFilter } from './lib/workflowGraph';
import { duplicateWorkflowNode } from './lib/workflowGraph';
import { streamEdgeStyle } from './lib/streamStyles';
import { loadWorkflow, parseWorkflowConfig, saveWorkflow, serializeWorkflow } from './lib/workflowStorage';
import { createInitialWorkflow, createOutputNode, NodeType, type NodeData, type WorkflowDocument, type WorkflowEdge, type WorkflowNode } from './nodes/NodeTypes';
import AdvancedWorkspace from './components/AdvancedWorkspace';
import AppHeader, { type EditorMode } from './components/AppHeader';
import BeginnerWorkspace from './components/BeginnerWorkspace';
import CommandPanel from './components/CommandPanel';
import FilterLibrary from './components/FilterLibrary';
import type { StreamType } from './nodes/NodeTypes';

const MODE_KEY = 'ffmpeg-editor-mode';
const SHELL_KEY = 'ffmpeg-shell-target';
const PROJECT_KEY = 'ffmpeg-project-name';

function readPreference<T extends string>(key: string, fallback: T, allowed: T[]): T {
  try {
    const value = window.localStorage.getItem(key) as T | null;
    return value && allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function readProjectName(): string {
  try { return window.localStorage.getItem(PROJECT_KEY) || '未命名工作流'; } catch { return '未命名工作流'; }
}

function downloadFile(filename: string, content: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const App: React.FC = () => {
  const [workflow, setWorkflow] = useState<WorkflowDocument>(() => loadWorkflow());
  const [mode, setMode] = useState<EditorMode>(() => readPreference(MODE_KEY, 'beginner', ['beginner', 'advanced']));
  const [shell, setShell] = useState<ShellTarget>(() => readPreference(SHELL_KEY, 'bash', ['bash', 'powershell']));
  const [projectName, setProjectName] = useState(readProjectName);
  const [selectedNodeId, setSelectedNodeId] = useState<string>();
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'error'>('saved');
  const [savedTime, setSavedTime] = useState('');
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState('');
  const [isCommandImportOpen, setCommandImportOpen] = useState(false);
  const [commandInput, setCommandInput] = useState('');
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<WorkflowNode, WorkflowEdge> | null>(null);
  const workflowFileRef = useRef<HTMLInputElement>(null);
  const commandDialogRef = useRef<HTMLDialogElement>(null);
  const notificationTimerRef = useRef<number | undefined>(undefined);

  const commandResult = useMemo(() => buildFFmpegCommand(workflow, shell), [workflow, shell]);
  const selectedNode = workflow.nodes.find((node) => node.id === selectedNodeId);

  useEffect(() => {
    setSaveState('saving');
    const timeout = window.setTimeout(() => {
      const saved = saveWorkflow(workflow);
      setSaveState(saved ? 'saved' : 'error');
      if (saved) setSavedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [workflow]);

  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
      window.localStorage.setItem(SHELL_KEY, shell);
      window.localStorage.setItem(PROJECT_KEY, projectName);
    } catch { /* Storage can be unavailable in private browsing. */ }
  }, [mode, shell, projectName]);

  useEffect(() => {
    const dialog = commandDialogRef.current;
    if (!dialog) return;
    if (isCommandImportOpen && !dialog.open) dialog.showModal();
    if (!isCommandImportOpen && dialog.open) dialog.close();
  }, [isCommandImportOpen]);

  useEffect(() => () => window.clearTimeout(notificationTimerRef.current), []);

  useEffect(() => {
    if (selectedNodeId && !workflow.nodes.some((node) => node.id === selectedNodeId)) setSelectedNodeId(undefined);
  }, [selectedNodeId, workflow.nodes]);

  const notify = useCallback((message: string) => {
    setNotification(message);
    window.clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = window.setTimeout(() => setNotification(''), 3800);
  }, []);

  const updateNode = useCallback((nodeId: string, patch: Partial<NodeData>) => {
    setWorkflow((current) => ({
      ...current,
      nodes: current.nodes.map((node) => node.id === nodeId
        ? { ...node, data: { ...node.data, ...patch, params: patch.params ? { ...node.data.params, ...patch.params } : node.data.params } }
        : node),
    }));
  }, []);

  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    setWorkflow((current) => {
      const removedIds = new Set(changes.filter((change): change is NodeRemoveChange => change.type === 'remove').map((change) => change.id));
      const nodes = applyNodeChanges(changes, current.nodes) as WorkflowNode[];
      return { ...current, nodes, edges: current.edges.filter((edge) => !removedIds.has(edge.source) && !removedIds.has(edge.target)) };
    });
  }, []);

  const handleEdgesChange = useCallback((changes: EdgeChange[]) => {
    setWorkflow((current) => ({ ...current, edges: applyEdgeChanges(changes, current.edges) as WorkflowEdge[] }));
  }, []);

  const validateConnection: IsValidConnection = useCallback((connection) => {
    const source = workflow.nodes.find((node) => node.id === connection.source);
    const target = workflow.nodes.find((node) => node.id === connection.target);
    return isStreamConnectionValid(source, target, connection.sourceHandle, connection.targetHandle);
  }, [workflow.nodes]);

  const handleConnect = useCallback((connection: Connection) => {
    const source = workflow.nodes.find((node) => node.id === connection.source);
    const target = workflow.nodes.find((node) => node.id === connection.target);
    if (!isStreamConnectionValid(source, target, connection.sourceHandle, connection.targetHandle) || !source || !target) return;
    const streamType: StreamType = source.type === NodeType.INPUT
      ? (connection.sourceHandle === 'audio' ? 'audio' : 'video')
      : source.data.streamType || (source.data.isAudio ? 'audio' : 'video');
    const edge: WorkflowEdge = {
      ...connection,
      id: `edge-${Date.now()}-${workflow.edges.length}`,
      source: connection.source as string,
      target: connection.target as string,
      sourceHandle: connection.sourceHandle || (source.type === NodeType.INPUT ? streamType : 'output'),
      targetHandle: connection.targetHandle || (target.type === NodeType.OUTPUT ? streamType : 'input'),
      type: 'smoothstep',
      data: { streamType },
      style: streamEdgeStyle(streamType),
    };
    setWorkflow((current) => ({ ...current, edges: addEdge(edge, current.edges) }));
  }, [workflow.edges.length, workflow.nodes]);

  const addPreset = useCallback((preset: FilterPreset) => {
    const id = nextNodeId(workflow.nodes, 'filter');
    const inserted = insertPreset(workflow, preset, id);
    setWorkflow(inserted.workflow);
    setSelectedNodeId(id);
  }, [workflow]);

  const handlePresetDragStart = useCallback((event: React.DragEvent<HTMLDivElement>, preset: FilterPreset) => {
    event.dataTransfer.setData('application/ffmpeg-preset', preset.id);
    event.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const presetId = event.dataTransfer.getData('application/ffmpeg-preset');
    const preset = FILTER_PRESETS.find((item) => item.id === presetId);
    if (!preset || !reactFlowInstance) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = reactFlowInstance.project({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });
    const nodeId = nextNodeId(workflow.nodes, 'filter');
    const node = createFilterNode(preset, nodeId, position);
    setWorkflow((current) => ({ ...current, nodes: [...current.nodes, node] }));
    setSelectedNodeId(nodeId);
  }, [reactFlowInstance, workflow.nodes]);

  const handleAddInput = useCallback(() => {
    const id = nextNodeId(workflow.nodes, 'input');
    const input = createInputNode(id, { x: 120, y: 400 + workflow.nodes.filter((node) => node.type === NodeType.INPUT).length * 170 });
    const output = workflow.nodes.find((node) => node.type === NodeType.OUTPUT);
    setWorkflow((current) => ({
      ...current,
      nodes: [...current.nodes, input],
      edges: output ? [...current.edges, ...connectInputToOutput(input, output)] : current.edges,
    }));
    setSelectedNodeId(id);
  }, [workflow.nodes]);

  const handleAddInputAt = useCallback((position: { x: number; y: number }) => {
    const flowPosition = reactFlowInstance?.project(position) || position;
    const id = nextNodeId(workflow.nodes, 'input');
    const input = createInputNode(id, flowPosition);
    const output = workflow.nodes.find((node) => node.type === NodeType.OUTPUT);
    setWorkflow((current) => ({
      ...current,
      nodes: [...current.nodes, input],
      edges: output ? [...current.edges, ...connectInputToOutput(input, output)] : current.edges,
    }));
    setSelectedNodeId(id);
  }, [reactFlowInstance, workflow.nodes]);

  const handleAddOutput = useCallback(() => {
    const outputCount = workflow.nodes.filter((node) => node.type === NodeType.OUTPUT).length;
    const id = nextNodeId(workflow.nodes, 'output');
    const output = createOutputNode(id, { x: 710, y: 208 + outputCount * 170 }, outputCount ? `output-${outputCount + 1}.mp4` : 'output.mp4');
    setWorkflow((current) => ({ ...current, nodes: [...current.nodes, output] }));
    setSelectedNodeId(id);
  }, [workflow.nodes]);

  const handleAddOutputAt = useCallback((position: { x: number; y: number }) => {
    const flowPosition = reactFlowInstance?.project(position) || position;
    const outputCount = workflow.nodes.filter((node) => node.type === NodeType.OUTPUT).length;
    const id = nextNodeId(workflow.nodes, 'output');
    const output = createOutputNode(id, flowPosition, outputCount ? `output-${outputCount + 1}.mp4` : 'output.mp4');
    setWorkflow((current) => ({ ...current, nodes: [...current.nodes, output] }));
    setSelectedNodeId(id);
  }, [reactFlowInstance, workflow.nodes]);

  const handleAddPresetAt = useCallback((preset: FilterPreset, position: { x: number; y: number }) => {
    const flowPosition = reactFlowInstance?.project(position) || position;
    const id = nextNodeId(workflow.nodes, 'filter');
    const node = createFilterNode(preset, id, flowPosition);
    setWorkflow((current) => ({ ...current, nodes: [...current.nodes, node] }));
    setSelectedNodeId(id);
  }, [reactFlowInstance, workflow.nodes]);

  const handleDuplicateNode = useCallback((nodeId: string) => {
    const source = workflow.nodes.find((node) => node.id === nodeId);
    if (!source) return;
    const duplicated = duplicateWorkflowNode(workflow, nodeId, nextNodeId(workflow.nodes, source.type === NodeType.INPUT ? 'input' : 'filter'));
    if (!duplicated) return;
    const newNode = duplicated.nodes[duplicated.nodes.length - 1];
    setWorkflow(duplicated);
    setSelectedNodeId(newNode.id);
    notify('节点及其连线已复制。');
  }, [workflow, notify]);

  const deleteNode = useCallback((nodeId: string) => {
    setWorkflow((current) => removeWorkflowNode(current, nodeId));
  }, []);

  const moveStep = useCallback((nodeId: string, streamType: StreamType, direction: -1 | 1) => {
    setWorkflow((current) => reorderStreamFilter(current, streamType, nodeId, direction));
  }, []);

  const exportConfig = useCallback(() => {
    downloadFile('ffmpeg-workflow.json', serializeWorkflow(workflow), 'application/json');
  }, [workflow]);

  const importConfigFile = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const imported = parseWorkflowConfig(await file.text());
      setWorkflow(imported);
      setSelectedNodeId(undefined);
      notify('工作流配置已导入。');
    } catch (error) {
      notify(error instanceof Error ? error.message : '无法读取工作流配置。');
    } finally {
      event.target.value = '';
    }
  }, [notify]);

  const importCommand = useCallback(() => {
    try {
      const imported = parseFFmpegCommand(commandInput);
      setWorkflow(imported.workflow);
      setImportWarnings(imported.warnings);
      setSelectedNodeId(undefined);
      setCommandImportOpen(false);
      setCommandInput('');
      if (imported.warnings.length) notify(`已导入，${imported.warnings.length} 项参数需要检查。`);
      else notify('FFmpeg 命令已转换为工作流。');
    } catch (error) {
      notify(error instanceof Error ? error.message : '无法解析这条 FFmpeg 命令。');
    }
  }, [commandInput, notify]);

  const copyCommand = useCallback(async () => {
    if (!commandResult.command) return;
    try {
      await navigator.clipboard.writeText(commandResult.command);
    } catch {
      const area = document.createElement('textarea');
      area.value = commandResult.command;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      area.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2400);
  }, [commandResult.command]);

  const downloadCommand = useCallback(() => {
    if (!commandResult.command) return;
    const bashScript = '#!/usr/bin/env bash\nset -e\n\n' + commandResult.command + '\n';
    const powershellScript = "$ErrorActionPreference = 'Stop'\n\n" + commandResult.command + '\n';
    downloadFile(shell === 'bash' ? 'ffmpeg-command.sh' : 'ffmpeg-command.ps1', shell === 'bash' ? bashScript : powershellScript, 'text/plain;charset=utf-8');
  }, [commandResult.command, shell]);

  const newWorkflow = useCallback(() => {
    if (!window.confirm('新建工作流会替换当前自动保存的内容。继续吗？')) return;
    setWorkflow(createInitialWorkflow());
    setSelectedNodeId(undefined);
    setProjectName('未命名工作流');
    notify('已新建工作流模板。');
  }, [notify]);

  const autoLayout = useCallback(() => {
    setWorkflow((current) => ({ ...current, nodes: layoutWorkflow(current.nodes, current.edges) }));
  }, []);

  const fitView = useCallback(() => reactFlowInstance?.fitView({ padding: 0.22, duration: 220, maxZoom: 1.15 }), [reactFlowInstance]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (saveWorkflow(workflow)) notify('工作流已保存。');
      } else if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        void copyCommand();
      } else if (event.key === '/' && !typing) {
        const search = document.querySelector<HTMLInputElement>('.filter-library .search-field input');
        if (search) { event.preventDefault(); search.focus(); }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [workflow, notify, copyCommand]);

  const openConfigImport = () => workflowFileRef.current?.click();
  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; };

  return (
    <div className="app-shell">
      <AppHeader
        mode={mode}
        projectName={projectName}
        saveState={saveState}
        savedTime={savedTime}
        onModeChange={setMode}
        onNameChange={setProjectName}
        onNew={newWorkflow}
        onImportConfig={openConfigImport}
        onExportConfig={exportConfig}
        onImportCommand={() => { setImportWarnings([]); setCommandImportOpen(true); }}
      />
      <input ref={workflowFileRef} name="workflow-config" type="file" accept="application/json,.json" className="visually-hidden" onChange={importConfigFile} />
      <main className={'workspace-shell workspace-shell--' + mode}>
        {mode === 'beginner' ? (
          <>
            <FilterLibrary mode="beginner" onAdd={addPreset} onDragStart={handlePresetDragStart} />
            <BeginnerWorkspace workflow={workflow} onUpdate={updateNode} onAddPreset={addPreset} onDelete={deleteNode} onMove={moveStep} />
          </>
        ) : (
          <AdvancedWorkspace
            workflow={workflow}
            selectedNode={selectedNode}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={handleConnect}
            onDrop={handleDrop}
            onDragOver={onDragOver}
            onPresetDragStart={handlePresetDragStart}
            onAddPreset={addPreset}
            onAddInput={handleAddInput}
            onAddInputAt={handleAddInputAt}
            onAddOutput={handleAddOutput}
            onAddOutputAt={handleAddOutputAt}
            onAddPresetAt={handleAddPresetAt}
            onDuplicateNode={handleDuplicateNode}
            onDeleteEdge={(edgeId) => handleEdgesChange([{ type: 'remove', id: edgeId }])}
            onUpdateNode={updateNode}
            onDeleteNode={deleteNode}
            onNodeClick={(_event, node) => setSelectedNodeId(node.id)}
            onInit={setReactFlowInstance}
            onFitView={fitView}
            onAutoLayout={autoLayout}
            isValidConnection={validateConnection}
          />
        )}
      </main>
      <CommandPanel result={commandResult} shell={shell} copied={copied} onShellChange={setShell} onCopy={copyCommand} onDownload={downloadCommand} />
      {notification && <div className="app-notification" role="status">{notification}</div>}
      <dialog ref={commandDialogRef} className="import-dialog" onCancel={(event) => { event.preventDefault(); setCommandImportOpen(false); }} onClose={() => setCommandImportOpen(false)}>
        <form method="dialog" onSubmit={(event) => { event.preventDefault(); importCommand(); }}>
          <div className="dialog-heading"><div><h2>导入 FFmpeg 命令</h2><p>解析常见输入、滤镜链与编码选项。</p></div><button type="button" className="icon-button" aria-label="关闭" onClick={() => setCommandImportOpen(false)}>×</button></div>
          <label className="control-field"><span className="control-field__label">命令内容</span><textarea name="command-import" value={commandInput} onChange={(event) => setCommandInput(event.target.value)} rows={6} placeholder={'ffmpeg -i input.mp4 -vf "scale=1280:720" -c:v libx264 output.mp4'} /></label>
          {importWarnings.length > 0 && <div className="import-warnings">上次导入提示：{importWarnings.join(' ')}</div>}
          <div className="dialog-actions"><button type="button" className="button-secondary" onClick={() => setCommandImportOpen(false)}>取消</button><button type="submit" className="button-primary" disabled={!commandInput.trim()}>解析并导入</button></div>
        </form>
      </dialog>
    </div>
  );
};

export default App;
